<script setup>
import {computed, getCurrentInstance} from 'vue'

const props = defineProps({
    editor: {
        type: Object,
        required: true
    }
})

const {proxy} = getCurrentInstance()
const store = proxy.$store

// --- Data ---

const buttonStyles = [
    {name: 'Primary', class: 'btn btn-primary', bg: 'bg-primary'},
    {name: 'Secondary', class: 'btn btn-secondary', bg: 'bg-secondary'},
    {name: 'Warning', class: 'btn btn-warning', bg: 'bg-warning text-dark'},
    {name: 'Info', class: 'btn btn-info', bg: 'bg-info text-dark'},
    {name: 'Light', class: 'btn btn-light', bg: 'bg-light border'},
    {name: 'Dark', class: 'btn btn-dark', bg: 'bg-dark'},
]

// --- Computed ---

const isMac = computed(() => {
    const platform = navigator.userAgentData?.platform || navigator.platform || ''
    return /Mac|iPhone|iPod|iPad/.test(platform)
})

const isDarkMode = computed(() => store.getters['user/pref']('darkMode'))

const modKey = computed(() => isMac.value ? '⌘' : 'Ctrl')

const sitename = computed(() => store.state.sitename)
const isOdp = computed(() => sitename.value.indexOf('outdoorsproject') === 0)

// --- Methods ---

const applyClass = (className) => {
    // 1. Determine if we are on a Heading or a Paragraph
    const isHeading = props.editor.isActive('heading')
    const nodeType = isHeading ? 'heading' : 'paragraph'

    // 2. Handle Button Styles (Links)
    if (className.includes('btn')) {
        const currentClass = props.editor.getAttributes('link').class
        props.editor.chain().focus()
            .extendMarkRange('link')
            .updateAttributes('link', {class: currentClass === className ? null : className})
            .run()
        return
    }

    // 3. Handle Block Styles (Lead, etc.)
    const currentAttrs = props.editor.getAttributes(nodeType)
    const newClass = currentAttrs.class === className ? null : className

    props.editor.chain().focus()
        .updateAttributes(nodeType, {class: newClass})
        .run()
}
</script>

<template>
    <div class="btn-group btn-group-sm" role="group" aria-label="Styles">
        <div class="dropdown">
            <button class="btn btn-sm dropdown-toggle" :class="{'btn-light': !isDarkMode}" type="button"
                    id="stylesDropdown" data-bs-toggle="dropdown" aria-expanded="false">
                <span v-if="editor.getAttributes('paragraph').class === 'lead'">Lead</span>
                <span v-else-if="editor.isActive('code')">Code</span>
                <span v-else>Styles</span>
            </button>
            <ul class="dropdown-menu" aria-labelledby="stylesDropdown">

                <template v-if="editor.isActive('link')">
                    <li class="dropdown-header">Button Styles</li>

                    <li v-for="style in buttonStyles" :key="style.class">
                        <a class="dropdown-item d-flex align-items-center" @click="applyClass(style.class)"
                           :class="{ 'active': editor.getAttributes('link').class === style.class }">
                            <span :class="['badge rounded-circle me-2', style.bg.replace('btn', 'bg')]"
                                  style="width: 10px; height: 10px; padding: 0;">&nbsp;</span>
                            <small>{{ style.name }} button</small>
                        </a>
                    </li>
                </template>

                <li class="dropdown-header">Block styles</li>

                <li>
                    <a class="dropdown-item" @click="applyClass('lead')"
                       :class="{ 'active': editor.getAttributes('paragraph').class === 'lead' }">
                        <i class="bi bi-fonts me-1"></i>
                        <small>Lead</small>
                    </a>
                </li>

                <li class="dropdown-header">Inline styles</li>

                <li>
                    <a class="dropdown-item d-flex justify-content-between align-items-center"
                       :class="{ 'active': editor.isActive('code') }"
                       @click="editor.chain().focus().toggleCode().run()">
                        <i class="bi bi-code me-1"></i>
                        <small>Code</small>
                        <span class="text-muted ms-5 small">{{ modKey }} E</span>
                    </a>
                </li>

                <template v-if="isOdp">
                    <li>
                        <a class="dropdown-item"
                           :class="{ 'active': editor.isActive('textStyle', { color: '#e74c3c' }) }"
                           :style="editor.isActive('textStyle', { color: '#e74c3c' }) ? {} : { color: '#e74c3c' }"
                           @click="editor.isActive('textStyle', { color: '#e74c3c' })
                             ? editor.chain().focus().unsetColor().run()
                             : editor.chain().focus().setColor('#e74c3c').run()"
                        >
                            <i class="bi bi-palette me-1"></i>
                            <small>Red</small>
                        </a>
                    </li>

                    <li>
                        <a class="dropdown-item"
                           :class="{ 'active': editor.isActive('textStyle', { color: '#27ae60' }) }"
                           :style="editor.isActive('textStyle', { color: '#27ae60' }) ? {} : { color: '#27ae60' }"
                           @click="editor.isActive('textStyle', { color: '#27ae60' })
                             ? editor.chain().focus().unsetColor().run()
                             : editor.chain().focus().setColor('#27ae60').run()"
                        >
                            <i class="bi bi-palette me-1"></i>
                            <small>Green</small>
                        </a>
                    </li>
                </template>
            </ul>
        </div>
    </div>
</template>

<style scoped>
.dropdown-header {
    border-bottom: 1px solid var(--bs-border-color);
    padding-bottom: 5px;
    font-size: 0.75rem;
    font-weight: 600;
}
</style>